import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import DrawerShell from '../components/DrawerShell';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Pill, Search, Plus, Package, RefreshCw, AlertTriangle, Filter, Activity, Building2, Info } from 'lucide-react';
import axios from 'axios';
import { formatDateIST as formatDate } from '../utils/date';
import CampusRoleShell from '../components/CampusRoleShell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  ordered: 'bg-blue-100 text-blue-700 border-blue-200',
  received: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
};

export default function PharmacyPage() {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [showRaiseDialog, setShowRaiseDialog] = useState(false);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/purchase-requests`, { withCredentials: true });
      setRequests(data || []);
    } catch (e) {
      toast.error('Failed to load requisitions');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const filtered = requests.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!r.medicine_name.toLowerCase().includes(q) &&
          !(r.composition || '').toLowerCase().includes(q) &&
          !(r.doctor_name || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const counts = {
    all: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    ordered: requests.filter(r => r.status === 'ordered').length,
    received: requests.filter(r => r.status === 'received').length,
    cancelled: requests.filter(r => r.status === 'cancelled').length,
  };

  const raiseActions = (
    <div className="flex items-center gap-2">
      <Button onClick={() => setShowRaiseDialog(true)} className="gap-2 bg-[#F15A22] hover:brightness-110 text-white" data-testid="raise-request-btn">
        <Plus className="h-4 w-4" /> Raise Request
      </Button>
      <Button variant="outline" size="sm" onClick={loadRequests} className="gap-1" data-testid="refresh-btn">
        <RefreshCw className="h-3.5 w-3.5" /> Refresh
      </Button>
    </div>
  );

  const pageInner = (
      <div className="flex-1 overflow-auto">
        <div className={`${role === 'admin' ? 'p-6 max-w-7xl mx-auto' : ''}`}>
          {/* Status filter cards */}
          <div className="grid grid-cols-5 gap-3 mb-5">
            {[
              { key: 'all', label: 'All', value: counts.all, color: 'border-gray-200' },
              { key: 'pending', label: 'Pending', value: counts.pending, color: 'border-amber-200' },
              { key: 'ordered', label: 'Ordered', value: counts.ordered, color: 'border-blue-200' },
              { key: 'received', label: 'Received', value: counts.received, color: 'border-emerald-200' },
              { key: 'cancelled', label: 'Cancelled', value: counts.cancelled, color: 'border-gray-200' },
            ].map(s => (
              <button key={s.key} onClick={() => setStatusFilter(s.key)}
                className={`bg-white rounded-xl border p-4 text-left transition-all ${
                  statusFilter === s.key ? 'border-emerald-400 ring-2 ring-emerald-100' : `${s.color} hover:border-gray-300`
                }`}
                data-testid={`status-filter-${s.key}`}>
                <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
            <div className="relative max-w-md">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by medicine, composition, or doctor..."
                className="pl-8 h-9 text-xs" data-testid="pharmacy-search" />
            </div>
          </div>

          {/* List */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-[2fr_1.5fr_1.2fr_80px_120px_100px] gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
              <div>Medicine</div>
              <div>Composition</div>
              <div>Raised By</div>
              <div className="text-right">Qty</div>
              <div>Status</div>
              <div>Date</div>
            </div>
            {loading ? (
              <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <Package className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No requisitions match your filter</p>
              </div>
            ) : (
              <div data-testid="pharmacy-req-rows-list">
                {filtered.map(r => (
                  <div key={r.id} className="grid grid-cols-[2fr_1.5fr_1.2fr_80px_120px_100px] gap-2 px-4 py-3 border-b border-gray-100 hover:bg-emerald-50/30 items-start text-sm" data-testid={`req-row-${r.id}`}>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{r.medicine_name}</p>
                      {r.patient_name && <p className="text-[10px] text-gray-500 truncate">For: {r.patient_name}</p>}
                      <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                        {r.priority && r.priority !== 'normal' && (
                          <span className={`inline-flex items-center text-[9px] font-bold px-1.5 py-0 rounded-full border ${
                            r.priority === 'urgent' ? 'bg-red-100 text-red-700 border-red-200 animate-pulse' :
                            r.priority === 'high' ? 'bg-amber-100 text-amber-800 border-amber-200' :
                            'bg-gray-100 text-gray-700 border-gray-200'
                          }`}>
                            {r.priority.toUpperCase()}
                          </span>
                        )}
                        {r.rx_7d > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-purple-700 bg-purple-50 px-1.5 py-0 rounded-full border border-purple-200">
                            <Activity className="h-2.5 w-2.5" />Rx {r.rx_7d}/wk
                          </span>
                        )}
                        {r.last_mrp > 0 && (
                          <span className="inline-flex items-center text-[9px] text-gray-500 bg-gray-100 px-1.5 py-0 rounded-full">
                            ₹{(r.last_mrp || 0).toFixed(2)} last
                          </span>
                        )}
                      </div>
                      {r.reason && (
                        <p className="text-[10px] text-amber-700 mt-0.5 italic line-clamp-1" title={r.reason}>
                          “{r.reason}”
                        </p>
                      )}
                    </div>
                    <div className="text-xs text-blue-600 truncate">{r.composition || '-'}</div>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-700 truncate">{r.doctor_name || '-'}</p>
                      <span className={`inline-block text-[9px] font-bold px-1.5 py-0 rounded mt-0.5 ${
                        r.raised_by_role === 'pharmacy'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {r.raised_by_role === 'pharmacy' ? 'Pharmacy' : 'Doctor'}
                      </span>
                    </div>
                    <div className="text-right font-semibold">{r.quantity}{r.unit ? ` ${r.unit}` : ''}</div>
                    <div>
                      <Badge className={`text-[10px] border ${STATUS_COLORS[r.status] || ''}`}>
                        {r.status}
                      </Badge>
                    </div>
                    <div className="text-xs text-gray-500">{formatDate(r.created_at)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
  );

  if (role === 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="pharmacy-page">
        <header className="bg-white border-b px-6 py-4 flex items-center justify-between">
          <h1 className="text-xl font-semibold text-[#071B34] font-heading">Pharmacy Console</h1>
          {raiseActions}
        </header>
        {pageInner}
        {showRaiseDialog && (
          <RaiseRequestDialog onClose={() => setShowRaiseDialog(false)} onCreated={() => { setShowRaiseDialog(false); loadRequests(); }} />
        )}
      </div>
    );
  }

  return (
    <CampusRoleShell
      pageTitle="Pharmacy Console"
      pageSubtitle="Purchase requisitions · stock requests"
      rightActions={raiseActions}
    >
      <div data-testid="pharmacy-page">{pageInner}</div>
      {showRaiseDialog && (
        <RaiseRequestDialog onClose={() => setShowRaiseDialog(false)} onCreated={() => { setShowRaiseDialog(false); loadRequests(); }} />
      )}
    </CampusRoleShell>
  );
}

// Search-driven medicine picker — pharmacy types a medicine name, picks from
// the inventory master, then enters the desired qty. The result list and
// the selected-medicine card surface stock, last-purchase price, last
// vendor, and recent Rx demand so pharmacy can make an informed decision
// without bouncing between screens.
function RaiseRequestDialog({ onClose, onCreated }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [quantity, setQuantity] = useState(1);
  // Priority defaults to "normal" — pharmacy bumps to High/Urgent for
  // out-of-stock items already being prescribed, or Low for nice-to-haves.
  const [priority, setPriority] = useState('normal');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [recentRxMap, setRecentRxMap] = useState({});
  const [lastPurchase, setLastPurchase] = useState(null);
  const abortRef = useRef(null);

  // Pull the recent-Rx histogram once on mount so the result list can
  // surface "Hot — 3 prescribed in last 7d" badges without per-row latency.
  useEffect(() => {
    axios.get(`${API_URL}/api/analytics/recent-prescriptions`, { withCredentials: true })
      .then(({ data }) => setRecentRxMap(data || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API_URL}/api/common/medicines`, {
          params: { search: query }, withCredentials: true, signal: ctrl.signal,
        });
        setResults((data || []).slice(0, 10));
      } catch (e) {
        if (e.name !== 'CanceledError' && e.code !== 'ERR_CANCELED') setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [query]);

  // When a medicine is selected, fetch its last-purchase info (MRP +
  // vendor) so pharmacy can sanity-check the price. Reset on de-select.
  useEffect(() => {
    if (!selected?.name) { setLastPurchase(null); return; }
    let cancelled = false;
    axios.post(`${API_URL}/api/analytics/last-purchase-info`,
      { names: [selected.name] }, { withCredentials: true })
      .then(({ data }) => {
        if (cancelled) return;
        const info = data?.[selected.name];
        setLastPurchase(info || null);
      })
      .catch(() => { if (!cancelled) setLastPurchase(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.name]);

  // Smart defaults whenever a medicine is picked: if stock is at/below
  // critical level AND there's recent prescribing demand, default qty to
  // 4 weeks of cover and bump priority to High. The pharmacist still has
  // final say.
  useEffect(() => {
    if (!selected) return;
    const rx7 = recentRxMap[selected.name] || 0;
    const stock = selected.stock || 0;
    if (rx7 > 0 && stock <= 10) {
      setQuantity(Math.min(100, Math.max(1, rx7 * 4)));
      if (stock <= 0) setPriority('urgent');
      else setPriority('high');
    }
  }, [selected, recentRxMap]);

  const handleSubmit = async () => {
    const name = (selected?.name || query).trim();
    if (!name) { toast.error('Enter or pick a medicine'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/purchase-requests`, {
        medicine_name: name,
        composition: selected?.composition || '',
        quantity: Number(quantity) || 1,
        unit: selected?.unit || '',
        priority,
        reason: reason.trim(),
        last_mrp: lastPurchase?.last_mrp || 0,
        rx_7d: recentRxMap[name] || 0,
      }, { withCredentials: true });
      toast.success(`Requisition raised for ${name}`);
      onCreated();
    } catch (e) {
      toast.error('Failed to raise requisition');
    }
    setSubmitting(false);
  };

  const stockBadgeClass = (s) =>
    s == null ? 'bg-gray-50 text-gray-400 border-gray-200' :
    s <= 0 ? 'bg-red-50 text-red-700 border-red-200' :
    s <= 10 ? 'bg-amber-50 text-amber-700 border-amber-200' :
    'bg-emerald-50 text-emerald-700 border-emerald-200';

  return (
    <DrawerShell
      open
      onClose={onClose}
      eyebrow="Procurement"
      title="Raise Purchase Requisition"
      subtitle="Search inventory master, review demand & pricing context, submit"
      accent="#047857"
      icon={<Plus className="h-5 w-5" />}
      width="lg"
      testId="raise-request-dialog"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || (!selected && query.trim().length < 2)}
            className="bg-emerald-700 hover:bg-emerald-800" data-testid="raise-submit">
            {submitting ? 'Raising...' : 'Raise Requisition'}
          </Button>
        </>
      }
    >
      <div className="p-5 space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Medicine</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input value={query} onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
                placeholder="Type to search 3,500+ medicines from inventory master..." className="pl-8 h-10"
                data-testid="raise-medicine-search" />
            </div>
            {selected ? (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-gray-900 truncate">{selected.name}</p>
                    {selected.composition && <p className="text-[11px] text-blue-700 truncate">{selected.composition}</p>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setSelected(null); setQuery(''); setPriority('normal'); setLastPurchase(null); }} className="text-xs h-7">Change</Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <ContextChip
                    label="Current Stock"
                    value={selected.stock == null ? '—' : selected.stock <= 0 ? 'NIL' : `${Math.floor(selected.stock)} ${selected.unit || 'Nos'}`}
                    color={stockBadgeClass(selected.stock)}
                    icon={<Package className="h-3 w-3" />}
                    sub={selected.stock != null && selected.stock <= 10 && selected.stock > 0 ? 'LOW STOCK' : selected.stock <= 0 ? 'OUT OF STOCK' : null}
                  />
                  <ContextChip
                    label="Demand (7 days)"
                    value={`${recentRxMap[selected.name] || 0} prescription${(recentRxMap[selected.name] || 0) === 1 ? '' : 's'}`}
                    color={recentRxMap[selected.name] >= 3 ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-gray-50 text-gray-600 border-gray-200'}
                    icon={<Activity className="h-3 w-3" />}
                    sub={recentRxMap[selected.name] >= 3 ? 'HIGH DEMAND' : null}
                  />
                  <ContextChip
                    label="Last Purchase Price"
                    value={lastPurchase ? `₹${(lastPurchase.last_mrp || 0).toFixed(2)}` : '—'}
                    color="bg-blue-50 text-blue-700 border-blue-200"
                    icon={<Info className="h-3 w-3" />}
                    sub={lastPurchase?.po_number || null}
                  />
                  <ContextChip
                    label="Last Vendor"
                    value={lastPurchase?.last_vendor || '—'}
                    color="bg-blue-50 text-blue-700 border-blue-200"
                    icon={<Building2 className="h-3 w-3" />}
                  />
                </div>
              </div>
            ) : results.length > 0 ? (
              <div className="border border-gray-200 rounded-md max-h-56 overflow-auto divide-y divide-gray-100" data-testid="raise-medicine-results">
                {results.map((r, i) => (
                  <button key={i} onClick={() => { setSelected(r); setQuery(r.name); }}
                    className="w-full px-3 py-2 hover:bg-emerald-50 text-left text-xs flex items-center justify-between gap-2"
                    data-testid={`raise-medicine-result-${i}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{r.name}</p>
                      {r.composition && <p className="text-[10px] text-blue-600 truncate">{r.composition}</p>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {(recentRxMap[r.name] || 0) > 0 && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200">
                          ↑{recentRxMap[r.name]}/wk
                        </span>
                      )}
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${stockBadgeClass(r.stock)}`}>
                        {r.stock == null ? '—' : r.stock <= 0 ? 'NIL' : `${Math.floor(r.stock)}`}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            ) : query.length >= 2 && !searching ? (
              <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                <AlertTriangle className="inline h-3 w-3 mr-1" />
                No master match. Submitting will raise a request as a &ldquo;new medicine&rdquo; with the typed name.
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Quantity *</Label>
              <Input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)}
                className="h-10" data-testid="raise-quantity" />
              {selected && (recentRxMap[selected.name] || 0) > 0 && (
                <p className="text-[10px] text-gray-500">Suggested: {Math.min(100, (recentRxMap[selected.name] || 0) * 4)} ({(recentRxMap[selected.name] || 0)}/wk × 4 weeks cover)</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Priority</Label>
              {/* 4 segmented buttons — colour-coded so urgency reads at a
                  glance in the Purchase team's queue. */}
              <div className="grid grid-cols-4 gap-1" data-testid="raise-priority">
                {[
                  { v: 'low', label: 'Low', cls: 'bg-gray-100 text-gray-700 border-gray-200', activeCls: 'bg-gray-700 text-white border-gray-700' },
                  { v: 'normal', label: 'Normal', cls: 'bg-blue-50 text-blue-700 border-blue-200', activeCls: 'bg-blue-700 text-white border-blue-700' },
                  { v: 'high', label: 'High', cls: 'bg-amber-50 text-amber-800 border-amber-200', activeCls: 'bg-amber-600 text-white border-amber-600' },
                  { v: 'urgent', label: 'Urgent', cls: 'bg-red-50 text-red-700 border-red-200', activeCls: 'bg-red-600 text-white border-red-600' },
                ].map(p => (
                  <button key={p.v} type="button" onClick={() => setPriority(p.v)}
                    className={`h-10 text-[11px] font-bold rounded-md border transition-colors ${priority === p.v ? p.activeCls : p.cls}`}
                    data-testid={`raise-priority-${p.v}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Reason / Note (optional)</Label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Patient on ongoing therapy, low stock, urgent ICU requirement..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none h-16 focus:ring-2 focus:ring-emerald-500"
              data-testid="raise-reason" />
          </div>
      </div>
    </DrawerShell>
  );
}

function ContextChip({ label, value, color, icon, sub }) {
  return (
    <div className={`flex items-start gap-2 px-2 py-1.5 rounded-md border ${color}`}>
      <span className="mt-0.5">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-[9px] uppercase tracking-wider font-bold opacity-70">{label}</p>
        <p className="text-xs font-bold truncate">{value}</p>
        {sub && <p className="text-[9px] font-semibold opacity-80 truncate">{sub}</p>}
      </div>
    </div>
  );
}
